import { supabase } from '../lib/supabase';

const BUCKET = 'financial-documents';

const resolveStorageUrl = (url, path) => {
  if (url?.trim()) return url.trim();
  if (path?.trim()) {
    const { data: { publicUrl } } = supabase.storage.from(BUCKET).getPublicUrl(path.trim());
    return publicUrl || null;
  }
  return null;
};

const normalizeRegionProfile = (row) => {
  if (!row) return null;
  return {
    id: row.id,
    region: row.region,
    financeContactName: row.finance_contact_name,
    financeContactEmail: row.finance_contact_email,
    financeContactPhone: row.finance_contact_phone,
    bankName: row.bank_name,
    bankBranch: row.bank_branch,
    accountName: row.account_name,
    accountNumber: row.account_number,
    swiftCode: row.swift_code,
    mobileMoneyProvider: row.mobile_money_provider,
    mobileMoneyNumber: row.mobile_money_number,
    taxIdentificationNumber: row.tax_identification_number,
    notes: row.notes,
    updatedAt: row.updated_at,
    createdAt: row.created_at,
  };
};

const normalizeMembershipRecord = (row, attachments = []) => ({
  id: row.id,
  entityType: row.entity_type,
  entityId: row.entity_id,
  entityName: row.entity_name,
  licenseNumber: row.license_number,
  region: row.region,
  membershipType: row.membership_type,
  amount: Number(row.amount || 0),
  currency: row.currency || 'TZS',
  paymentStatus: row.payment_status,
  paymentMethod: row.payment_method,
  paymentReference: row.payment_reference,
  receiptNumber: row.receipt_number,
  paidAt: row.paid_at,
  dueDate: row.due_date,
  seasonYear: row.season_year,
  notes: row.notes,
  recordedBy: row.recorded_by,
  createdAt: row.created_at,
  updatedAt: row.updated_at,
  attachments: attachments.map(normalizeAttachment),
});

const normalizeAttachment = (row) => ({
  id: row.id,
  membershipRecordId: row.membership_record_id,
  attachmentType: row.attachment_type,
  fileName: row.file_name,
  filePath: row.file_path,
  fileUrl: resolveStorageUrl(row.file_url, row.file_path),
  fileSize: row.file_size,
  createdAt: row.created_at,
});

export const ENTITY_TYPE_OPTIONS = [
  { value: 'player', label: 'Player' },
  { value: 'coach', label: 'Coach' },
  { value: 'official', label: 'Official / Referee' },
  { value: 'regional_player', label: 'Regional Player' },
  { value: 'club', label: 'Club Team' },
  { value: 'other', label: 'Other' },
];

export const PAYMENT_STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending' },
  { value: 'partial', label: 'Partially Paid' },
  { value: 'paid', label: 'Paid' },
  { value: 'overdue', label: 'Overdue' },
  { value: 'waived', label: 'Waived' },
  { value: 'refunded', label: 'Refunded' },
];

export const MEMBERSHIP_TYPE_OPTIONS = [
  { value: 'annual_license', label: 'Annual License Fee' },
  { value: 'registration_fee', label: 'Registration Fee' },
  { value: 'renewal', label: 'License Renewal' },
  { value: 'membership_dues', label: 'Membership Dues' },
  { value: 'other', label: 'Other' },
];

export const ATTACHMENT_TYPE_OPTIONS = [
  { value: 'receipt', label: 'Receipt' },
  { value: 'invoice', label: 'Invoice' },
  { value: 'proof_of_payment', label: 'Proof of Payment' },
  { value: 'bank_slip', label: 'Bank Slip' },
  { value: 'other', label: 'Other' },
];

export const financeService = {
  async getRegionFinanceProfiles() {
    const { data, error } = await supabase
      .from('region_finance_profiles')
      .select('*')
      .order('region', { ascending: true });
    if (error) throw new Error(error.message || 'Failed to load region finance profiles');
    return (data || []).map(normalizeRegionProfile);
  },

  async upsertRegionFinanceProfile(profile) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const payload = {
      region: profile.region,
      finance_contact_name: profile.financeContactName?.trim() || null,
      finance_contact_email: profile.financeContactEmail?.trim() || null,
      finance_contact_phone: profile.financeContactPhone?.trim() || null,
      bank_name: profile.bankName?.trim() || null,
      bank_branch: profile.bankBranch?.trim() || null,
      account_name: profile.accountName?.trim() || null,
      account_number: profile.accountNumber?.trim() || null,
      swift_code: profile.swiftCode?.trim() || null,
      mobile_money_provider: profile.mobileMoneyProvider?.trim() || null,
      mobile_money_number: profile.mobileMoneyNumber?.trim() || null,
      tax_identification_number: profile.taxIdentificationNumber?.trim() || null,
      notes: profile.notes?.trim() || null,
      updated_by: user.id,
      updated_at: new Date().toISOString(),
    };

    const { data, error } = await supabase
      .from('region_finance_profiles')
      .upsert(payload, { onConflict: 'region' })
      .select()
      .single();

    if (error) throw new Error(error.message || 'Failed to save region finance profile');
    return normalizeRegionProfile(data);
  },

  async getEntityFinancialRecords({
    entityType,
    entityId,
    entityName,
    region,
    regionOnly = false,
  } = {}) {
    if (regionOnly && region) {
      const { data, error } = await supabase
        .from('license_membership_records')
        .select('*')
        .eq('region', region)
        .order('season_year', { ascending: false, nullsFirst: false })
        .order('created_at', { ascending: false });

      if (error) throw new Error(error.message || 'Failed to load regional financial records');
      return (data || []).map((row) => normalizeMembershipRecord(row, []));
    }

    const collected = [];
    const seen = new Set();

    const appendRows = (rows = []) => {
      rows.forEach((row) => {
        if (!seen.has(row.id)) {
          seen.add(row.id);
          collected.push(normalizeMembershipRecord(row, []));
        }
      });
    };

    if (entityId && entityType) {
      const { data, error } = await supabase
        .from('license_membership_records')
        .select('*')
        .eq('entity_type', entityType)
        .eq('entity_id', entityId)
        .order('season_year', { ascending: false, nullsFirst: false })
        .order('created_at', { ascending: false });

      if (error) throw new Error(error.message || 'Failed to load entity financial records');
      appendRows(data || []);
    }

    if (entityName?.trim() && entityType) {
      const { data, error } = await supabase
        .from('license_membership_records')
        .select('*')
        .eq('entity_type', entityType)
        .eq('entity_name', entityName.trim())
        .order('season_year', { ascending: false, nullsFirst: false })
        .order('created_at', { ascending: false });

      if (error) throw new Error(error.message || 'Failed to load entity financial records');
      appendRows(data || []);
    }

    return collected.sort((a, b) => {
      const seasonA = a.seasonYear || '';
      const seasonB = b.seasonYear || '';
      if (seasonA !== seasonB) return seasonB.localeCompare(seasonA);
      return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
    });
  },

  async getMembershipRecords(filters = {}) {
    let query = supabase
      .from('license_membership_records')
      .select('*')
      .order('created_at', { ascending: false });

    if (filters.entityType) query = query.eq('entity_type', filters.entityType);
    if (filters.paymentStatus) query = query.eq('payment_status', filters.paymentStatus);
    if (filters.region) query = query.eq('region', filters.region);
    if (filters.seasonYear) query = query.eq('season_year', filters.seasonYear);

    const { data, error } = await query;
    if (error) throw new Error(error.message || 'Failed to load membership records');

    const records = data || [];
    if (!records.length) return [];

    const ids = records.map((r) => r.id);
    const { data: attachments } = await supabase
      .from('financial_attachments')
      .select('*')
      .in('membership_record_id', ids);

    const attachmentMap = {};
    (attachments || []).forEach((att) => {
      if (!attachmentMap[att.membership_record_id]) attachmentMap[att.membership_record_id] = [];
      attachmentMap[att.membership_record_id].push(att);
    });

    return records.map((row) => normalizeMembershipRecord(row, attachmentMap[row.id] || []));
  },

  async createMembershipRecord(record) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const payload = {
      entity_type: record.entityType,
      entity_id: record.entityId || null,
      entity_name: record.entityName?.trim(),
      license_number: record.licenseNumber?.trim() || null,
      region: record.region || null,
      membership_type: record.membershipType || 'annual_license',
      amount: Number(record.amount || 0),
      currency: record.currency || 'TZS',
      payment_status: record.paymentStatus || 'pending',
      payment_method: record.paymentMethod?.trim() || null,
      payment_reference: record.paymentReference?.trim() || null,
      receipt_number: record.receiptNumber?.trim() || null,
      paid_at: record.paidAt || null,
      due_date: record.dueDate || null,
      season_year: record.seasonYear?.trim() || null,
      notes: record.notes?.trim() || null,
      recorded_by: user.id,
    };

    const { data, error } = await supabase
      .from('license_membership_records')
      .insert(payload)
      .select()
      .single();

    if (error) throw new Error(error.message || 'Failed to create membership record');
    return normalizeMembershipRecord(data, []);
  },

  async updateMembershipRecord(id, record) {
    const payload = {
      entity_type: record.entityType,
      entity_id: record.entityId || null,
      entity_name: record.entityName?.trim(),
      license_number: record.licenseNumber?.trim() || null,
      region: record.region || null,
      membership_type: record.membershipType || 'annual_license',
      amount: Number(record.amount || 0),
      currency: record.currency || 'TZS',
      payment_status: record.paymentStatus || 'pending',
      payment_method: record.paymentMethod?.trim() || null,
      payment_reference: record.paymentReference?.trim() || null,
      receipt_number: record.receiptNumber?.trim() || null,
      paid_at: record.paidAt || null,
      due_date: record.dueDate || null,
      season_year: record.seasonYear?.trim() || null,
      notes: record.notes?.trim() || null,
      updated_at: new Date().toISOString(),
    };

    const { data, error } = await supabase
      .from('license_membership_records')
      .update(payload)
      .eq('id', id)
      .select()
      .single();

    if (error) throw new Error(error.message || 'Failed to update membership record');

    const { data: attachments } = await supabase
      .from('financial_attachments')
      .select('*')
      .eq('membership_record_id', id);

    return normalizeMembershipRecord(data, attachments || []);
  },

  async deleteMembershipRecord(id) {
    const { error } = await supabase.from('license_membership_records').delete().eq('id', id);
    if (error) throw new Error(error.message || 'Failed to delete membership record');
  },

  async uploadAttachment(recordId, file, attachmentType = 'receipt') {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');
    if (!file) throw new Error('File is required');

    const timestamp = Date.now();
    const sanitized = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
    const filePath = `membership-records/${recordId}/${timestamp}-${sanitized}`;

    const { error: uploadError } = await supabase.storage
      .from(BUCKET)
      .upload(filePath, file, { cacheControl: '3600', upsert: false });

    if (uploadError) throw new Error(uploadError.message || 'Failed to upload file');

    const { data: { publicUrl } } = supabase.storage.from(BUCKET).getPublicUrl(filePath);

    const { data, error } = await supabase
      .from('financial_attachments')
      .insert({
        membership_record_id: recordId,
        attachment_type: attachmentType,
        file_name: file.name,
        file_path: filePath,
        file_url: publicUrl,
        file_size: file.size,
        uploaded_by: user.id,
      })
      .select()
      .single();

    if (error) throw new Error(error.message || 'Failed to save attachment metadata');
    return normalizeAttachment(data);
  },

  async deleteAttachment(attachmentId, filePath) {
    if (filePath) {
      await supabase.storage.from(BUCKET).remove([filePath]);
    }
    const { error } = await supabase.from('financial_attachments').delete().eq('id', attachmentId);
    if (error) throw new Error(error.message || 'Failed to delete attachment');
  },

  computeSummary(records = [], regionProfiles = []) {
    const totalRecords = records.length;
    const paidRecords = records.filter((r) => r.paymentStatus === 'paid');
    const pendingRecords = records.filter((r) => ['pending', 'partial', 'overdue'].includes(r.paymentStatus));
    const totalCollected = paidRecords.reduce((sum, r) => sum + (r.amount || 0), 0);
    const totalOutstanding = records
      .filter((r) => r.paymentStatus !== 'paid' && r.paymentStatus !== 'waived')
      .reduce((sum, r) => sum + (r.amount || 0), 0);

    const byEntityType = ENTITY_TYPE_OPTIONS.map(({ value, label }) => ({
      label,
      value: records.filter((r) => r.entityType === value).length,
    })).filter((item) => item.value > 0);

    const byStatus = PAYMENT_STATUS_OPTIONS.map(({ value, label }) => ({
      label,
      value: records.filter((r) => r.paymentStatus === value).length,
    })).filter((item) => item.value > 0);

    return {
      totalRecords,
      paidCount: paidRecords.length,
      pendingCount: pendingRecords.length,
      regionsConfigured: regionProfiles.filter((p) => p.bankName || p.accountNumber).length,
      totalCollected,
      totalOutstanding,
      byEntityType,
      byStatus,
    };
  },
};
